import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SHOPPER_HOME } from '../constants/routes';

interface BackState {
  from?: string;
}

/**
 * Back navigation that survives deep links, refreshes and redirect hops.
 *
 * Prefers an explicit origin passed via `navigate(to, { state: { from } })`,
 * falls back to browser history, and finally to a known-good route so back
 * never strands the shopper on an unrelated page or outside the app.
 */
export function useSmartBack(fallback: string = SHOPPER_HOME) {
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as BackState | null)?.from;

  return useCallback(() => {
    if (from) {
      navigate(from);
      return;
    }

    // idx is React Router's position in its own history stack; 0 means this
    // entry is the first one we created, so there is nothing of ours to pop.
    const idx = (window.history.state as { idx?: number } | null)?.idx;
    if (typeof idx === 'number' ? idx > 0 : window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate(fallback, { replace: true });
  }, [navigate, from, fallback]);
}
